import { flow, isUndefined, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { AccessGroup, AccessGroupActionTypes, AccessGroupSection, AccessGroupState } from './types';

const initialAccessGroup: AccessGroup = {
    accessGroupName: '',
    description: '',
    entityIds: [],
    userIds: [],
    documentNameIds: [],
    roleIds: []
};

export const INITIAL_STATE: AccessGroupState = {
    allAccessGroups: [],
    fetchingAccessGroups: false,
    currentAccessGroup: initialAccessGroup,
    savedAccessGroup: initialAccessGroup,
    accessGroupModalOpen: false,
    selectedAccessGroupSection: AccessGroupSection.GENERAL,
    entitiesForAccessGroups: [],
    accessGroupUserColumnSort: undefined,
    accessGroupUserFilters: {},
    accessGroupEntityColumnSort: undefined,
    accessGroupEntityFilters: {},
    accessGroupDocumentColumnSort: undefined,
    accessGroupDocumentFilters: {},
    accessGroupDocumentGroupColumnSort: undefined,
    accessGroupDocumentGroupFilters: {},
    accessGroupRoleColumnSort: undefined,
    accessGroupRoleFilters: {},
    groupNameIsUnique: false,
    accessGroupUpdated: false,
    isSavingAccessGroup: false,
    accessGroupDocumentNames: [],
    allAccessGroupDocumentGroups: [],
    isFetchingEntities: false,
    isFetchingDocuments: false,
    isFetchingDocumentGroups: false,
    deleteConfirmationDialogOpen: false,
    deletingAccessGroup: false
};

export const accessGroupReducer: Reducer<AccessGroupState> = (state = INITIAL_STATE, { type, payload }): AccessGroupState => {
    switch (type) {
        case AccessGroupActionTypes.FETCH_ALL_ACCESS_GROUPS_STARTED:
            return set('fetchingAccessGroups', true, state);
        case AccessGroupActionTypes.FETCH_ALL_ACCESS_GROUPS_SUCCESSFUL: {
            return flow(
                set('allAccessGroups', payload),
                set('fetchingAccessGroups', false)
            )(state);
        }
        case AccessGroupActionTypes.FETCH_ALL_ACCESS_GROUPS_FAILED:
            return set('fetchingAccessGroups', false, state);
        case AccessGroupActionTypes.TOGGLE_ACCESS_GROUP_MODAL: {
            const group = isUndefined(payload) ? initialAccessGroup : payload;
            return flow(
                set('currentAccessGroup', group),
                set('savedAccessGroup', group),
                set('accessGroupModalOpen', !state.accessGroupModalOpen)
            )(state);
        }
        case AccessGroupActionTypes.SELECT_ACCESS_GROUP_SECTION:
            return set('selectedAccessGroupSection', payload, state);
        case AccessGroupActionTypes.UPDATE_ACCESS_GROUP: {
            const { key, value } = payload;
            return set(`currentAccessGroup[${key}]`, value, state);
        }
        case AccessGroupActionTypes.FETCH_ALL_ENTITIES_FOR_ACCESS_GROUPS_STARTED:
            return set('isFetchingEntities', true, state);
        case AccessGroupActionTypes.FETCH_ALL_ENTITIES_FOR_ACCESS_GROUPS_SUCCESSFUL:
            return flow(
                set('entitiesForAccessGroups', payload),
                set('isFetchingEntities', false)
            )(state);
        case AccessGroupActionTypes.FETCH_ALL_ENTITIES_FOR_ACCESS_GROUPS_FAILED:
            return set('isFetchingEntities', false, state);
        case AccessGroupActionTypes.TOGGLE_ACCESS_GROUP_COLUMN_SORT: {
            const { table, columnSort } = payload;
            return set(`accessGroup${table}ColumnSort`, columnSort, state);
        }
        case AccessGroupActionTypes.SET_ACCESS_GROUP_FILTERS: {
            const { table, type, key, value } = payload;
            const isTextFilter = type === 'text';
            return flow(
                set(`accessGroup${table}Filters[${key}][${type}]`, value),
                set(`accessGroup${table}Filters[${key}][${isTextFilter ? 'dropdown' : 'text'}]`, isTextFilter ? null : '')
            )(state);
        }
        case AccessGroupActionTypes.CLEAR_ACCESS_GROUP_FILTERS:
            return flow(
                set(`accessGroup${payload.table}Filters`, {}),
                set(`accessGroup${payload.table}ColumnSort`, undefined)
            )(state);
        case AccessGroupActionTypes.SET_GROUP_NAME_IS_UNIQUE:
            return set('groupNameIsUnique', payload, state);
        case AccessGroupActionTypes.SET_ACCESS_GROUP_UPDATED:
            return set('accessGroupUpdated', payload, state);
        case AccessGroupActionTypes.SAVE_ACCESS_GROUP_STARTED:
            return flow(
                set('isSavingAccessGroup', true),
                set('fetchingAccessGroups', true)
            )(state);
        case AccessGroupActionTypes.SAVE_ACCESS_GROUP_SUCCESSFUL: {
            return flow(
                set('allAccessGroups', payload),
                set('isSavingAccessGroup', false),
                set('accessGroupUpdated', false),
                set('fetchingAccessGroups', false)
            )(state);
        }
        case AccessGroupActionTypes.SAVE_ACCESS_GROUP_FAILED:
            return flow(
                set('isSavingAccessGroup', false),
                set('fetchingAccessGroups', false)
            )(state);
        case AccessGroupActionTypes.FETCH_DOCUMENT_NAMES_FOR_ACCESS_GROUPS_STARTED:
            return set('isFetchingDocuments', true, state);
        case AccessGroupActionTypes.FETCH_DOCUMENT_NAMES_FOR_ACCESS_GROUPS_SUCCESSFUL:
            return flow(
                set('accessGroupDocumentNames', payload),
                set('isFetchingDocuments', false)
            )(state);
        case AccessGroupActionTypes.FETCH_DOCUMENT_NAMES_FOR_ACCESS_GROUPS_FAILED:
            return set('isFetchingDocuments', false, state);
        case AccessGroupActionTypes.FETCH_DOCUMENT_GROUPS_FOR_ACCESS_GROUPS_STARTED:
            return set('isFetchingDocumentGroups', true, state);
        case AccessGroupActionTypes.FETCH_DOCUMENT_GROUPS_FOR_ACCESS_GROUPS_SUCCESSFUL:
            return flow(
                set('allAccessGroupDocumentGroups', payload),
                set('isFetchingDocumentGroups', false)
            )(state);
        case AccessGroupActionTypes.FETCH_DOCUMENT_GROUPS_FOR_ACCESS_GROUPS_FAILED:
            return set('isFetchingDocumentGroups', false, state);
        case AccessGroupActionTypes.TOGGLE_DELETE_ACCESS_GROUP_CONFIRMATION_MODAL: {
            const { accessGroup } = payload;
            const selectedAccessGroup = accessGroup || initialAccessGroup;
            return flow(
                set('deleteConfirmationDialogOpen', !isUndefined(accessGroup)),
                set('currentAccessGroup', selectedAccessGroup),
                set('savedAccessGroup', selectedAccessGroup)
            )(state);
        }
        case AccessGroupActionTypes.DELETE_ACCESS_GROUP_STARTED:
            return set('deletingAccessGroup', true, state);
        case AccessGroupActionTypes.DELETE_ACCESS_GROUP_SUCCESSFUL:
            return flow(
                set('allAccessGroups', payload),
                set('deletingAccessGroup', false),
                set('deleteConfirmationDialogOpen', false)
            )(state);
        case AccessGroupActionTypes.DELETE_ACCESS_GROUP_FAILED:
            return set('deletingAccessGroup', false, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
